import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal, Eyebrow, EASE } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import { useStore } from "@/context/StoreContext";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(null);
  const { addToList, inList } = useStore();

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    setActiveImg(null);
    api.get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        document.title = `${r.data.name} — THE GOOD GRID`;
        api.get("/products", { params: { category: r.data.category } })
          .then((res) => setRelated(res.data.filter((p) => p.id !== r.data.id).slice(0, 3)))
          .catch(() => {});
      })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div data-testid="product-not-found" className="bg-charcoal text-ivory min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <h1 className="font-serif text-4xl mb-4">This piece has moved.</h1>
        <Link to="/collections" className="text-champagne text-[11px] font-mono uppercase tracking-[0.22em] border-b border-champagne/50 pb-1">Back to collections</Link>
      </div>
    );
  }

  if (!product) {
    return <div data-testid="product-loading" className="bg-charcoal min-h-screen" />;
  }

  const added = inList(product.id);
  const details = [
    { label: "Minimum Order", value: product.moq },
    { label: "Estimated Lead Time", value: product.lead_time },
    { label: "Packaging", value: product.packaging },
    { label: "Custom Branding", value: product.custom_branding ? "Available" : "On request" },
  ].filter((d) => d.value);

  return (
    <div data-testid="product-detail-page" className="bg-charcoal text-ivory min-h-screen pt-24 lg:pt-32 pb-24">
      <div className="px-4 sm:px-8 lg:px-16">
        <Link to="/collections" data-testid="product-back" className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.22em] text-ivory/50 hover:text-champagne transition-colors mb-10">
          <ArrowLeft size={14} /> Collections
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20">
          <div className="lg:sticky lg:top-28">
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: EASE }}
              className="relative overflow-hidden bg-charcoal-soft aspect-[4/5]"
            >
              <img src={activeImg || product.image} alt={product.name} className="w-full h-full object-cover opacity-90" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#121212]/50 to-transparent" />
              {product.custom_branding && (
                <div className="absolute top-5 left-5 text-[10px] font-mono uppercase tracking-[0.2em] text-charcoal bg-champagne px-3 py-1.5">Brandable</div>
              )}
            </motion.div>
            {product.gallery?.length > 1 && (
              <div data-testid="product-gallery" className="grid grid-cols-4 gap-3 mt-3">
                {product.gallery.map((g) => (
                  <button
                    key={g.image}
                    data-testid={`gallery-${g.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => setActiveImg(g.image)}
                    className={`group text-left ${(activeImg || product.image) === g.image ? "" : "opacity-70 hover:opacity-100"} transition-opacity`}
                  >
                    <div className={`overflow-hidden aspect-square border ${(activeImg || product.image) === g.image ? "border-champagne" : "border-white/10"}`}>
                      <img src={g.image} alt={`${product.name} — ${g.label}`} loading="lazy" className="w-full h-full object-cover" />
                    </div>
                    <p className={`text-[9px] font-mono uppercase tracking-[0.15em] mt-1.5 ${(activeImg || product.image) === g.image ? "text-champagne" : "text-ivory/40"}`}>{g.label}</p>
                  </button>
                ))}
              </div>
            )}
          </div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.15, ease: EASE }}>
            <Eyebrow>{product.brand}</Eyebrow>
            <h1 data-testid="product-name" className="font-serif text-4xl sm:text-5xl mt-4 tracking-tight leading-tight">{product.name}</h1>
            <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-ivory/50 mt-3">{product.category}</p>
            <p data-testid="product-price" className="font-serif text-2xl text-champagne-light mt-6">Price on Request</p>
            <p className="text-ivory/60 leading-relaxed mt-6 max-w-lg">{product.description}</p>

            {product.specs?.length > 0 && (
              <div className="mt-8">
                <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-champagne mb-3">Details</p>
                <ul className="space-y-2">
                  {product.specs.map((s) => (
                    <li key={s} className="flex gap-3 text-sm text-ivory/60">
                      <span className="w-1 h-1 bg-champagne rotate-45 mt-2 shrink-0" />{s}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {product.colors?.length > 0 && (
              <div className="mt-6">
                <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-champagne mb-3">Available Finishes</p>
                <div className="flex flex-wrap gap-2">
                  {product.colors.map((c) => (
                    <span key={c} className="text-[10px] font-mono uppercase tracking-[0.15em] border border-white/15 text-ivory/60 px-3 py-1.5">{c}</span>
                  ))}
                </div>
              </div>
            )}

            {product.occasions?.length > 0 && (
              <div className="mt-6">
                <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-champagne mb-3">Corporate Suitability</p>
                <div className="flex flex-wrap gap-2">
                  {product.occasions.map((o) => (
                    <span key={o} className="text-[10px] font-mono uppercase tracking-[0.15em] bg-white/5 text-ivory/70 px-3 py-1.5">{o}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-10 border-t border-white/10 divide-y divide-white/10">
              {details.map((d) => (
                <div key={d.label} className="py-4 flex justify-between gap-6 text-sm">
                  <span className="text-ivory/40">{d.label}</span>
                  <span className="text-ivory text-right">{d.value}</span>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link
                to={`/request-quote?product=${product.id}`}
                data-testid="product-request-quote"
                className="flex-1 flex items-center justify-center gap-2 bg-champagne text-charcoal text-[11px] font-mono uppercase tracking-[0.22em] px-6 py-4 hover:bg-champagne-light transition-colors duration-300"
              >
                Request Corporate Quote <ArrowUpRight size={14} />
              </Link>
              <button
                data-testid="product-add-to-enquiry"
                onClick={() => {
                  if (added) return;
                  addToList(product);
                  toast.success(`${product.name} added to your gifting list`);
                }}
                className={`flex-1 flex items-center justify-center gap-2 border text-[11px] font-mono uppercase tracking-[0.22em] px-6 py-4 transition-colors duration-300 ${
                  added ? "border-champagne text-champagne" : "border-ivory/25 text-ivory hover:border-champagne hover:text-champagne"
                }`}
              >
                {added ? <Check size={14} /> : <Plus size={14} />} {added ? "In Gifting List" : "Add to Enquiry"}
              </button>
            </div>
            <p className="text-ivory/30 text-xs mt-6 leading-relaxed">
              Sample listing — specifications, pricing and availability are confirmed by our team during quotation.
            </p>
          </motion.div>
        </div>

        {related.length > 0 && (
          <div className="mt-28">
            <Reveal><Eyebrow>Continue Exploring</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-3xl sm:text-4xl mt-4 mb-12 tracking-tight">More in {product.category}</h2>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-14">
              {related.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.08}>
                  <ProductCard product={p} index={i} />
                </Reveal>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
